import MomentUtils from '@date-io/moment';
import { PointOfSale } from '@m4a3/api-client';
import { Dow, OpeningHour } from '@m4a3/api-client/dist/models/shared/opening-hours.interface';
import { padStart } from "lodash";
import { DateTime } from 'luxon';

export const dayNumberToNameMap: { [key: number]: Dow } = {
  0: Dow.SUNDAY,
  1: Dow.MONDAY,
  2: Dow.TUESDAY,
  3: Dow.WEDNESDAY,
  4: Dow.THURSDAY,
  5: Dow.FRIDAY,
  6: Dow.SATURDAY,
};

export const formatMomentDateToOpeningHourString = (momentDate) => {
  const asDate = momentDate.toDate();
  return `${padStart(asDate.getUTCHours().toString(), 2, '0')}:${padStart(asDate.getUTCMinutes().toString(), 2, '0')}:${padStart(asDate.getUTCSeconds().toString(), 2, '0')}`;
};

export function convertUtcToLocalTime(utcTimeString) {
  // Create a Moment object with the UTC time string and format
  const utcTime = new MomentUtils().moment.utc(utcTimeString, 'HH:mm:ss');

  // Convert the UTC time to local time
  const localTime = utcTime.local();

  // Format the local time as a string in the same format (HH:mm:ss)
  return localTime.format('HH:mm:ss');
}

export const getOpeningHoursForDay = (
  dayDate: Date,
  pointOfSale: PointOfSale
) => {
  if (
    !pointOfSale ||
    !pointOfSale.openingHours ||
    !Array.isArray(pointOfSale.openingHours)
  ) {
    return null;
  }

  const openingHoursForDay = pointOfSale.openingHours.find(
    (openingHour) => openingHour.dow === dayNumberToNameMap[dayDate.getDay()]
  );

  return openingHoursForDay;
};

const getAllOpeningHoursForToday = (pointOfSales: PointOfSale[]) => {
  const today = new Date();
  return pointOfSales
    .map(pointOfSale => getOpeningHoursForDay(today, pointOfSale))
};

const filterClosedOrInvalidOpeningHours = (openingHours: OpeningHour[]): OpeningHour[] => {
  return openingHours
    .filter(Boolean)
    .filter((openingHour: OpeningHour) => {
    return openingHour.endTime !== openingHour.startTime;
  });
}

export const isPointOfSaleClosedToday = (pointOfSale: PointOfSale): boolean => {
  const today = new Date();
  const openingHours = getOpeningHoursForDay(today, pointOfSale);

  return !openingHours || openingHours?.startTime === openingHours?.endTime;
}

const getSortedTimes = (pointOfSales: PointOfSale[], timezone: string, timeType: 'startTime' | 'endTime') => {
  const openingHours = getAllOpeningHoursForToday(pointOfSales);
  const validTimes = filterClosedOrInvalidOpeningHours(openingHours);
  const times = validTimes.map(openingHour => openingHour?.[timeType]);

  const timesInZone = times.map(time => DateTime.fromISO(time, { zone: 'utc' }).setZone(timezone));

  return timesInZone.sort((a, b) => timeType === 'startTime' ? a.valueOf() - b.valueOf() : b.valueOf() - a.valueOf());
};

export const getEarliestOpeningTime = (pointOfSales: PointOfSale[], timezone: string): string => {
  const sortedTimes = getSortedTimes(pointOfSales, timezone, 'startTime');
  return sortedTimes.length > 0 ? sortedTimes[0].toUTC().toJSDate().toISOString() : null;
}

export const getLatestClosingTime = (pointOfSales: PointOfSale[], timezone: string): string => {
  const sortedTimes = getSortedTimes(pointOfSales, timezone, 'endTime');
  return sortedTimes.length > 0 ? sortedTimes[0].toUTC().toJSDate().toISOString() : null;
}
